import { Check, NOTE_CUT, TrackerSong, analyse, finalizeSound } from "../../shared/tracker";
import { encodeBrr } from "../../shared/brr";
import { writeIt } from "../../shared/it_writer";
import { midiFile, renderSong, wavFile, zipFiles } from "../../shared/tracker_render";
import { byId, flashToast } from "../dom";
import { t } from "../i18n";
import { state } from "../tracker_store";
import { earOn } from "./tracker_sounds";

// the export tab: what the SNES will think of the song, then the six files. everything is made here in the app, so it works
// the same on the website and in the desktop app. the game file stays locked while a check is red in SNES safe mode

const slug = (text: string): string => text.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 32) || "song";

function save(name: string, data: Uint8Array | string, type: string): void {
  const blob = new Blob([typeof data === "string" ? data : (data as Uint8Array<ArrayBuffer>)], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function say(text: string): void {
  byId("tk-export-note").textContent = text;
  flashToast(text);
}

interface Format {
  id: string;
  title: string;
  tag: string;
  detail: string;
  main?: boolean;
  locked?: (blocked: boolean) => boolean;
  run: (song: TrackerSong) => void;
}

const FORMATS: Format[] = [
  {
    id: "it",
    title: "Game music .it",
    tag: "for PVSnesLib",
    detail: "The file your friend converts with smconv. Instruments, uncompressed samples, 8 voices at most.",
    main: true,
    locked: (blocked) => blocked,
    run: (song) => {
      try {
        const made = writeIt(song);
        save(`${slug(song.title)}.it`, made.bytes, "application/octet-stream");
        const lowered = made.report.loweredNotes;
        say(lowered ? t("saved {name}.it. {n} notes were lowered to stay under the 128 kHz limit", { name: slug(song.title), n: lowered }) : t("saved {name}.it", { name: slug(song.title) }));
      } catch (error) {
        say(error instanceof Error ? t(error.message) : t("could not make that file"));
      }
    }
  },
  {
    id: "wav",
    title: "Song audio .wav",
    tag: "listen",
    detail: "The whole song as one sound file, to share or check.",
    run: (song) => {
      const done = renderSong(song, { rate: 44100, snes: earOn(), tail: 1 });
      save(`${slug(song.title)}.wav`, wavFile(done.samples, done.rate), "audio/wav");
      say(t("saved {name}.wav", { name: slug(song.title) }));
    }
  },
  {
    id: "voices",
    title: "Each voice .wav",
    tag: "zip",
    detail: "One file per voice, for mixing in another program.",
    run: (song) => {
      const done = renderSong(song, { rate: 44100, snes: earOn(), stems: true, tail: 1 });
      const files = done.voices
        .map((samples, index) => ({ name: `voice${index + 1}.wav`, data: samples.some((value) => Math.abs(value) > 0.0001) ? wavFile(samples, done.rate) : null }))
        .filter((file): file is { name: string; data: Uint8Array } => Boolean(file.data));
      if (!files.length) return say(t("nothing to export yet, add some notes"));
      save(`${slug(song.title)}_voices.zip`, zipFiles(files), "application/zip");
      say(t("saved {n} voice files", { n: files.length }));
    }
  },
  {
    id: "brr",
    title: "SNES sounds .brr",
    tag: "raw",
    detail: "Every sound already squeezed into the SNES format. Made by this app, not checked on real hardware.",
    run: (song) => {
      if (!song.sounds.length) return say(t("no sounds to export"));
      const lines: string[] = [];
      const files = song.sounds.map((sound, index) => {
        const final = finalizeSound(sound);
        const data = encodeBrr(final.data, final.loop ? final.loopStart / 16 : -1);
        const name = `${String(index + 1).padStart(2, "0")}_${slug(sound.name)}.brr`;
        lines.push(`${name}  ${final.rate} Hz  ${data.length} bytes${final.loop ? `  loops from block ${final.loopStart / 16}` : ""}`);
        return { name, data };
      });
      files.push({ name: "sounds.txt", data: new TextEncoder().encode(lines.join("\n") + "\n") });
      save(`${slug(song.title)}_sounds.zip`, zipFiles(files), "application/zip");
      say(t("saved {n} sounds", { n: song.sounds.length }));
    }
  },
  {
    id: "mid",
    title: "Notes only .mid",
    tag: "move tune",
    detail: "Just the notes, no sounds, to open in other music programs.",
    run: (song) => {
      const hasNotes = song.patterns.some((pattern) => pattern.notes.some((note) => note.n !== NOTE_CUT));
      if (!hasNotes) return say(t("nothing to export yet, add some notes"));
      save(`${slug(song.title)}.mid`, midiFile(song), "audio/midi");
      say(t("saved {name}.mid", { name: slug(song.title) }));
    }
  },
  {
    id: "json",
    title: "Project backup .json",
    tag: "editable",
    detail: "Everything, so you can reopen it from the song list and keep editing.",
    run: (song) => {
      save(`${slug(song.title)}.json`, JSON.stringify(song), "application/json");
      say(t("saved {name}.json", { name: slug(song.title) }));
    }
  }
];

function badge(check: Check): string {
  return check.state === "ok" ? "OK" : check.state === "warn" ? "!!" : "NO";
}

export function renderExport(): void {
  const song = state.song;
  if (!song) return;
  const report = analyse(song);
  const list = byId("tk-checks");
  list.replaceChildren(...report.checks.map((check) => {
    const item = document.createElement("li");
    item.className = check.state === "ok" ? "" : check.state;
    const mark = document.createElement("b");
    mark.textContent = badge(check);
    const text = document.createElement("span");
    text.textContent = `${t(check.title)}: ${t(check.detail)}`;
    if (check.fix) {
      const fix = document.createElement("small");
      fix.textContent = t(check.fix);
      text.append(fix);
    }
    item.append(mark, text);
    return item;
  }));
  const holder = byId("tk-formats");
  holder.replaceChildren(...FORMATS.map((format) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "tk-format" + (format.main ? " main" : "");
    button.disabled = Boolean(format.locked?.(report.blocked));
    const title = document.createElement("b");
    title.textContent = t(format.title);
    const tag = document.createElement("em");
    tag.textContent = t(format.tag);
    title.append(tag);
    const detail = document.createElement("span");
    detail.textContent = t(format.detail);
    button.append(title, detail);
    button.addEventListener("click", () => state.song && format.run(state.song));
    return button;
  }));
  const note = byId("tk-export-note");
  if (report.blocked) note.textContent = song.mode === "snes" ? t("fix the red items to unlock the game file, or switch to Free mode") : t("fix the red items before exporting");
  else if (song.mode === "free") note.textContent = t("Free mode: the file may not work on a real SNES");
  else note.textContent = t("ready for the SNES converter");
}
